import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { SproutCommonContent } from "../../../../modules/content/components/SproutCommonContent/SproutCommonContent";
import { SproutMedia } from "../../../../modules/content/components/SproutMedia/SproutMedia";
import { SproutRichText } from "../../../../modules/content/components/SproutRichText/SproutRichText";
import { SproutSection } from "../../../../modules/content/components/SproutSection/SproutSection";
import type { ITeamMemberGridBlockProps } from "./TeamMemberGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const TeamMemberGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITeamMemberGridBlockProps) => {
  if (!items?.length) return null;
  const Link = getComponent("Link");

  return (
    <SproutSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <SproutCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-x-6 gap-y-12", columns[columnCount || 3])}>
          {items.map((member, index) => (
            <article className="group flex h-full flex-col" key={index}>
              <SproutMedia
                aspectRatio="3:4"
                className={cn(
                  "sprout-paper-lift rounded-[48%_48%_1.25rem_1.25rem] border-[0.5rem] border-whiteboard",
                  index % 3 === 0 && "-rotate-1 bg-coral",
                  index % 3 === 1 && "rotate-1 bg-sky",
                  index % 3 === 2 && "-rotate-[0.5deg] bg-sunflower",
                )}
                imageClassName="rounded-[46%_46%_0.8rem_0.8rem] transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                media={member.portrait.image}
                sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              />
              <div className="flex flex-1 flex-col px-3 pt-6">
                <h3 className="font-sprout-display text-2xl font-bold leading-tight">
                  {member.name}
                </h3>
                {member.role ? (
                  <p className="mt-2 font-sprout-mono text-xs text-primary">{member.role}</p>
                ) : null}
                {member.biography ? (
                  <SproutRichText
                    className="mt-4 text-base text-basalt md:text-lg"
                    content={member.biography}
                  />
                ) : null}
                {member.links?.length ? (
                  <div className="mt-auto flex flex-wrap gap-4 border-t border-weathered-steel pt-3">
                    {member.links.map((link, linkIndex) => (
                      <Link
                        aria-label={link.accessibilityLabel || undefined}
                        className="inline-flex min-h-11 items-center font-sprout-mono text-xs text-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                        href={link.href}
                        key={linkIndex}
                        rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                        target={link.openInNewTab ? "_blank" : undefined}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </SproutSection>
  );
};

TeamMemberGridBlock.displayName = "Block::TeamMemberGridBlock";
